import { useState } from 'react';
import { X } from 'lucide-react';
import { SwipaSend as Send } from '@/icons/SwipaSend';
import { useChatStore } from '@/store/chatStore';
import { useQuery } from '@tanstack/react-query';
import { friendsApi } from '@/api/friends';
import { messagesApi } from '@/api/messages';
import { Hash, Lock, User } from 'lucide-react';

interface Props {
  messageId: string;
  /** Phase 11.3 — when set, this component does a bulk forward of all listed
   *  messages instead of just the one. messageId is used as preview anchor. */
  messageIds?: string[];
  preview?: string;
  onClose: () => void;
}

export default function ForwardModal({ messageId, messageIds, preview, onClose }: Props) {
  const { joinedChannels } = useChatStore();
  const [silent, setSilent] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: friends } = useQuery({
    queryKey: ['friends'],
    queryFn: () => friendsApi.list(),
  });

  const forwardTo = async (target: { toChannelId?: string; toPeerUserId?: string }, key: string) => {
    setBusy(key);
    setError(null);
    try {
      if (messageIds && messageIds.length > 1) {
        await messagesApi.bulkForward({ messageIds, ...target, silent });
      } else {
        await messagesApi.forwardMessage(messageId, { ...target, silent });
      }
      onClose();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Не удалось переслать');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-md p-5 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-surface-100 font-semibold flex items-center gap-2"><Send size={16} /> Переслать</h3>
          <button onClick={onClose} className="text-surface-500 hover:text-surface-200"><X size={18} /></button>
        </div>

        {preview && (
          <div className="text-xs text-surface-400 bg-surface-800 border border-surface-700 rounded-lg p-2 mb-3 line-clamp-3">
            {preview}
          </div>
        )}

        <label className="flex items-center gap-2 mb-3 text-xs text-surface-300">
          <input type="checkbox" checked={silent} onChange={(e) => setSilent(e.target.checked)} className="accent-brand-500" />
          Без уведомления
        </label>

        {error && <div className="bg-red-900/40 border border-red-800 text-red-300 text-xs rounded p-2 mb-2">{error}</div>}

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-3">
          {joinedChannels.length > 0 && (
            <div>
              <p className="text-xs uppercase text-surface-600 mb-1">Каналы</p>
              {joinedChannels.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => forwardTo({ toChannelId: ch.id }, `c-${ch.id}`)}
                  disabled={busy !== null}
                  className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg hover:bg-surface-800 text-sm text-surface-200 disabled:opacity-50"
                >
                  {ch.type === 'PRIVATE' ? <Lock size={13} /> : <Hash size={13} />}
                  <span className="truncate">{ch.displayName || ch.name}</span>
                  {busy === `c-${ch.id}` && <span className="ml-auto text-xs text-surface-500">…</span>}
                </button>
              ))}
            </div>
          )}

          {friends && friends.length > 0 && (
            <div>
              <p className="text-xs uppercase text-surface-600 mb-1">Друзья</p>
              {friends.map((f) => (
                <button
                  key={f.friendshipId}
                  onClick={() => forwardTo({ toPeerUserId: f.friend.id }, `u-${f.friend.id}`)}
                  disabled={busy !== null}
                  className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-lg hover:bg-surface-800 text-sm text-surface-200 disabled:opacity-50"
                >
                  <User size={13} />
                  <span className="truncate">{f.friend.displayName}</span>
                  <span className="text-surface-600 text-xs font-mono truncate">@{f.friend.username}</span>
                  {busy === `u-${f.friend.id}` && <span className="ml-auto text-xs text-surface-500">…</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
